/**
 * Axios Configuration
 * Central HTTP client setup with interceptors for authentication and error handling
 */

import axios, { AxiosError, AxiosResponse } from 'axios';
import type { ApiError } from '@/types/api/common';
import { ErrorService } from '@/lib/errors';

// API base URL from environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// Timeout configuration (30 seconds)
const REQUEST_TIMEOUT = 30000;

// Track if we've already shown the session expired warning
let sessionExpiredWarningShown = false;

/**
 * Create Axios instance with default configuration
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

/**
 * Request interceptor
 * Adds authentication token and other headers to outgoing requests
 *
 * Note: The `lang` query parameter is NOT auto-injected here.
 * Per API spec, only location and bank search endpoints support `lang`.
 * Those endpoints handle `lang` explicitly in their API service methods
 * (location.api.ts and bank.api.ts).
 */
apiClient.interceptors.request.use(
  (config) => {
    // Session-based auth: cookies are sent automatically with withCredentials: true
    // No need to manually add Authorization header

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Handles common error scenarios and response transformations
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful response in development
    if (import.meta.env.DEV) {
      console.log(
        `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
        {
          status: response.status,
          data: response.data,
        }
      );
    }

    return response;
  },
  (error: AxiosError<ApiError>) => {
    // Handle authentication errors globally
    if (error.response?.status === 401) {
      // Unauthorized - clear stored user profile
      clearUserProfile();

      // In development, don't redirect - let devAutoLogin handle re-authentication
      if (import.meta.env.DEV) {
        // Only show warning once to avoid spam
        if (!sessionExpiredWarningShown) {
          console.warn('[Auth] Session expired - please refresh the page to re-authenticate');
          sessionExpiredWarningShown = true;
          // Reset after 10 seconds
          setTimeout(() => {
            sessionExpiredWarningShown = false;
          }, 10000);
        }
      } else {
        // In production, session expired - redirect to login
        // Only redirect if not already on login page
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }

    // Log errors in development
    if (import.meta.env.DEV) {
      const errorInfo = ErrorService.parseError(error);
      console.error('[API Error]', {
        code: errorInfo.code,
        message: errorInfo.message,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
    }

    // Return the error for handling at the call site
    // Note: We don't show notifications here to allow components to handle errors specifically
    return Promise.reject(error);
  }
);

/**
 * Session-based authentication - cookies are handled by browser
 * These helper functions are kept for compatibility but no longer needed
 */

/**
 * Helper function to check if user is authenticated
 * Note: With session cookies, we can't check auth status client-side
 * The server will return 401 if not authenticated
 */
export const isAuthenticated = (): boolean => {
  // Session auth: check if user data exists in localStorage/state
  const userProfile = localStorage.getItem('userProfile');
  return userProfile !== null;
};

/**
 * Helper function to store user profile
 */
export const setUserProfile = (user: unknown): void => {
  localStorage.setItem('userProfile', JSON.stringify(user));
};

/**
 * Helper function to get user profile
 */
export const getUserProfile = (): unknown | null => {
  const profile = localStorage.getItem('userProfile');
  if (!profile) return null;
  try {
    return JSON.parse(profile);
  } catch (error) {
    console.warn('Failed to parse userProfile from localStorage:', error);
    localStorage.removeItem('userProfile');
    return null;
  }
};

/**
 * Helper function to clear user profile
 */
export const clearUserProfile = (): void => {
  localStorage.removeItem('userProfile');
};

export default apiClient;
